/**
 * OcrolusNoteCard — the loan's bank-analysis note, in place on the Ocrolus
 * surface.
 *
 * The backend writes this note itself (app/services/ocrolus_note.py): accounts,
 * cash-flow rollup and document-authenticity signals, rebuilt IN PLACE on every
 * Ocrolus update with the prior copy pushed onto `versions[]`. It arrives as the
 * third key of `GET /notes/{dealId}` alongside underwriting/processing, is
 * flagged `systemManaged: true`, and has no POST — so this is read-only by
 * contract, not by choice. See eastview-os
 * frontend_documentation/ocrolus-book-and-notes.md §3.
 *
 * The same note also shows as a read-only tab in LoanNotesPane, where notes
 * normally live. This card is the copy in context, next to the statements that
 * produced it.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import Box from "@mui/material/Box";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { useNotesHook } from "../../../../../hooks/useNotesHook";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import colors from "../../../../../theme/colors";
import { OcrolusCard, OcrolusEmptyState } from "./ocrolusShared";

// Mirrors the note preview in LoanNotesPane so the same HTML reads the same way
// in both places.
const NOTE_SX = {
  fontSize: "12.5px",
  fontFamily: "var(--primaryFont)",
  color: colors.slate600,
  lineHeight: 1.55,
  "& h3": { fontSize: "14px", color: colors.ink, margin: "0 0 0.5em 0" },
  "& h4": { fontSize: "12.5px", color: colors.ink, margin: "1em 0 0.35em 0" },
  "& p": { margin: "0 0 0.4em 0" },
  "& ul, & ol": { paddingLeft: "1.4em", margin: "0.25em 0" },
  // The bank-accounts table. Its markup is written server-side with inline
  // styles (`border-collapse:collapse`, 2px cell padding), and an inline style
  // beats a stylesheet rule — hence the `!important`s. `separate` is what lets
  // the rounded corners render at all: a collapsed table draws its borders on
  // the cells, so the radius has nothing to clip.
  "& table": {
    width: "100%",
    borderCollapse: "separate !important",
    borderSpacing: 0,
    border: `1px solid ${colors.slate200}`,
    borderRadius: "8px",
    overflow: "hidden",
    margin: "0.4em 0 0.9em",
  },
  "& th, & td": {
    textAlign: "left",
    padding: "6px 10px !important",
    borderBottom: `1px solid ${colors.slate200}`,
  },
  "& th": {
    background: colors.slate50,
    fontSize: "10.5px",
    fontWeight: 600,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: colors.slate500,
    whiteSpace: "nowrap",
  },
  // No hairline hanging under the last row against the rounded bottom edge.
  "& tr:last-child td": { borderBottom: "none" },
  "& a": { color: colors.action },
};

// Newest-first: the current note as the head, then prior `versions[]` (the
// backend caps the history at 20).
const buildVersions = (note) => {
  if (!note?.version) return [];
  const past = Array.isArray(note.versions) ? [...note.versions] : [];
  past.sort((a, b) => (b.version || 0) - (a.version || 0));
  return [
    {
      version: note.version,
      htmlString: note.htmlString || "",
      updatedBy: note.updatedBy || note.createdBy,
      updatedAt: note.updatedAt || note.createdAt,
      isCurrent: true,
    },
    ...past,
  ];
};

const OcrolusNoteCard = ({ dealId, noteStamp }) => {
  const { getNotes } = useNotesHook();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  const load = useCallback(() => {
    if (!dealId) return;
    setLoading(true);
    getNotes({
      dealId,
      onSuccessFn: (res) => {
        setNote(res?.ocrolus || null);
        setSelectedVersion(null); // snap back to current on every refresh
      },
      onCompleteFn: () => setLoading(false),
    });
    // getNotes is re-created each render (the hook isn't memoized).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // `noteStamp` is the book status' ocrolusNote.updatedAt — when Ocrolus
  // rewrites the note, the status poll sees it first and this refetches.
  useEffect(() => {
    load();
  }, [load, noteStamp]);

  const versions = useMemo(() => buildVersions(note), [note]);
  const shown = useMemo(
    () =>
      versions.find((v) => v.version === selectedVersion) || versions[0] || null,
    [versions, selectedVersion],
  );

  return (
    <OcrolusCard
      title="Bank analysis note"
      endContent={
        <>
          {versions.length > 0 && (
            <Select
              size="small"
              value={shown?.version ?? ""}
              onChange={(e) => setSelectedVersion(e.target.value)}
              sx={{ fontSize: 12, minWidth: 160, height: 32 }}
            >
              {versions.map((v) => (
                <MenuItem key={v.version} value={v.version} sx={{ fontSize: 12 }}>
                  {`Version ${v.version}${v.isCurrent ? " · current" : ""}`}
                </MenuItem>
              ))}
            </Select>
          )}
          <Tooltip title="Written and rewritten by the Ocrolus pipeline — there is no edit path, by design.">
            <Chip
              size="small"
              icon={<AutoAwesomeOutlined sx={{ fontSize: 14 }} />}
              label="System managed"
              variant="outlined"
              sx={{ height: 24, fontSize: 11, color: colors.slate600 }}
            />
          </Tooltip>
        </>
      }
    >
      {loading && !note ? (
        <Flx ac jc sx={{ py: 5 }}>
          <CircularProgress size={20} />
        </Flx>
      ) : !shown?.htmlString ? (
        <OcrolusEmptyState icon={InsightsOutlined} title="No analysis yet">
          This note is written automatically once statements are processed by
          Ocrolus. It updates itself — no edits needed.
        </OcrolusEmptyState>
      ) : (
        <Box sx={{ px: 2, py: 1.75 }}>
          <Box sx={NOTE_SX} dangerouslySetInnerHTML={{ __html: shown.htmlString }} />
          <Txt
            sx={{ fontSize: 11, color: colors.slate400, mt: 1.5 }}
            component="div"
          >
            {`v${shown.version} · updated ${formatDateTime(shown.updatedAt)}`}
            {shown.isCurrent ? "" : " · historical version"}
          </Txt>
        </Box>
      )}
    </OcrolusCard>
  );
};

export default OcrolusNoteCard;
